/**
 * **净资产曲线**（J 域 J2 · 端侧绘制层）
 *
 * 依据：J 域 PRD **§4.3**（三档 / 只画 `live` / 无快照月份不画）· **§4.7**（诚实性）· `DR-012`。
 *
 * ## 🔴 本组件的**全部职责 = 把"诚实性"落在绘制层**（四条约束，缺一条都会撒谎）
 *
 * | # | 约束 | 若不遵守，用户会看到什么 |
 * |:--:|---|---|
 * | 1 | 🔴 **只绘 `origin === 'live'` 的点** | 回填点是**只有账户层**的口径（保单/持仓/手动无历史值）⇒ 与 `live` 混在一起画会形成"历史跳升"，而那个跳升**不是真实变化**。⚠️ **筛选必须在绘制层** —— 服务端**故意**不过滤（`backfill` 行是历史事实的记录、也是将来口径补齐后的重绘依据） |
 * | 2 | 🔴 `enoughForCurve === false` ⇒ **画空态，不画线** | 两点连一条线看起来像"净资产在涨"，而那只是"数据太少"。**空态文案必须是"数据积累中"**，不是"暂无数据"（后者让人以为坏了） |
 * | 3 | 🔴 **不补点、不平推、不外推** | 这是 `DR-012` 拍板原话的点名对象（"过去的不能预测将来的曲线"）；v1 的"五桶平推"整体废弃 |
 * | 4 | 🔴 **每个点用它自己的 `baseCurrency` 渲染**（批 `121` / `DR-068`） | 账本切过本币后，曲线是**混币**的：拿响应顶层 `baseCurrency` 统一渲染，会把 `$1,000` 印成 `¥1,000` —— 即 `DR-036` D5 明禁的「把外币伪装成本币」。变更点另画竖线 + chip，**不折算旧点**（`DR-020` §5 D2） |
 *
 * ## ⚠️ 为什么曲线颜色取 `cat-1` 而不是"涨跌色"
 *
 * 净资产是**家底数字**，不是"涨/跌"的比较结果 —— J 域 PRD §4.8 明文「资产/负债总计用**中性强调色**」，
 * 且原型里给「总资产」挂的 `class="down"` 是**死类**（实测无 CSS 定义）⇒ 已拍板删掉。
 * ⇒ 曲线用**分类色板首色**（`theme.jiaying.chart[0]`），与"红涨绿跌"（用于收支）**分开**。
 */

import { Box, Chip, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CurveRange,
  type CurveRange as CurveRangeType,
  type NetWorthCurveResponse,
} from '@jiaying/shared';
import { getNetWorthCurve } from '../api/endpoints.js';
import { currencySwitchMarks, splitCurrencySegments } from '../features/currency-segments.js';
import { toChartPoints, type ChartPoint } from '../features/networth-curve-points.js';

/** 三档的中文标签（🔴 取值来自契约的 `CurveRange`，不另写一份档位定义） */
const RANGE_LABEL: Record<CurveRangeType, string> = {
  [CurveRange.M12]: '近 12 月',
  [CurveRange.M36]: '近 3 年',
  [CurveRange.All]: '全部',
};

export interface NetWorthChartProps {
  range: CurveRangeType;
  onRangeChange: (range: CurveRangeType) => void;
}

export function NetWorthChart({ range, onRangeChange }: NetWorthChartProps) {
  const theme = useTheme();
  const curve = useQuery({
    // ⚠️ `range` 进 key ⇒ 切档会各自缓存（切回来不重新请求）
    queryKey: ['networth-curve', range],
    queryFn: () => getNetWorthCurve(range),
  });

  const data = useMemo<ChartPoint[]>(() => {
    const points: NetWorthCurveResponse['points'] = curve.data?.points ?? [];
    // 🔴 约束 1（只绘 live）+ 逐点取**自己的**币种 —— 两条都在 `toChartPoints()` 里，
    //    抽出去是为了能直测（Tooltip 文本在静态渲染里看不到，留在组件内就测不了）。
    return toChartPoints(points);
    // 🔴 约束 3：不排序以外的任何加工 —— 契约已保证 points 升序，这里也不补点
  }, [curve.data]);

  /**
   * 🔴 本币切换的分段与变更点（`DR-068` §2 D3）。
   *
   * 分段基于**实际绘制的点**（`data`，已过滤 `live`）—— 用未过滤的点会让
   * "回填点上的币种变化"产生一条**画不出来**的竖线（图上有标记、但那段没有线）。
   */
  const segments = useMemo(() => splitCurrencySegments(data), [data]);
  const marks = useMemo(() => currencySwitchMarks(segments), [segments]);

  /** 是否够画线 —— 🔴 两个条件都要：`enoughForCurve`（服务端口径）+ 过滤后仍有 ≥2 点（否则连不成线） */
  const canDraw = (curve.data?.enoughForCurve ?? false) && data.length >= 2;

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          净资产曲线
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={range}
          onChange={(_e, v: CurveRangeType | null) => { if (v !== null) onRangeChange(v); }}
        >
          {(Object.keys(RANGE_LABEL) as CurveRangeType[]).map((r) => (
            <ToggleButton key={r} value={r}>{RANGE_LABEL[r]}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/*
        🔴 本币切换点标注（`DR-068` §2 D3）—— 每个变更点一枚 chip，
        与图内的竖分隔线**成对**（都由 `currencySwitchMarks()` 产出，不会一多一少）。
        ⚠️ 只在**真发生过切换**时出现：单币种账本永远看不到这一行（不制造视觉噪音）。
      */}
      {marks.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }} data-testid="currency-switch-marks">
          {marks.map((m) => (
            <Chip
              key={m.date}
              size="small"
              variant="outlined"
              color="default"
              label={m.label}
              data-testid="currency-switch-chip"
            />
          ))}
        </Box>
      )}

      {curve.isPending && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
          读取中…
        </Typography>
      )}

      {curve.isError && (
        <Typography variant="body2" color="error" sx={{ py: 6, textAlign: 'center' }}>
          曲线读取失败：{String(curve.error)}
        </Typography>
      )}

      {curve.isSuccess && !canDraw && (
        // 🔴 约束 2：空态**不画线**，且文案是"数据积累中"（不是"暂无数据" —— 后者像坏了）
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            数据积累中
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            曲线只画**真实快照**（每日自动记录）—— 不估算、不平推。
            {data.length === 1 && '已有 1 个点，至少 2 个点才会连成线。'}
          </Typography>
        </Box>
      )}

      {curve.isSuccess && canDraw && (
        <Box sx={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
              <CartesianGrid stroke={theme.jiaying.border.base} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                // 只显示 月-日（年份在 12 月档里重复且挤）
                tickFormatter={(d: string) => d.slice(5)}
                tick={{ fontSize: 11, fill: theme.jiaying.text.tertiary }}
                stroke={theme.jiaying.border.base}
              />
              <YAxis
                tickFormatter={(v: number) => v.toLocaleString('zh-CN')}
                tick={{ fontSize: 11, fill: theme.jiaying.text.tertiary }}
                stroke={theme.jiaying.border.base}
                width={64}
              />
              <Tooltip
                formatter={(_v, _n, item) => [(item.payload as ChartPoint).text, '净资产']}
                // ⚠️ Recharts 的 `label` 类型是 `ReactNode`（不是 `string`）—— 直接 `(d: string)` 会报
                //    "Type 'ReactNode' is not assignable to type 'string'"。这里按契约收窄：我们的
                //    `dataKey="date"` 恒是 `YYYY-MM-DD` 字符串。
                labelFormatter={(label) => String(label)}
              />
              {/*
                🔴 本币切换的竖分隔线 —— 画在**新币种段的第一个点**上。
                ⚠️ 它只是"视觉分隔"，**不做任何插值/折算**：线两侧的点各自保留自己的币种与数值
                （这正是 `DR-020` §5 D2「曲线不重算 + 变更点标注」的字面实现）。
              */}
              {marks.map((m) => (
                <ReferenceLine
                  key={m.date}
                  x={m.date}
                  stroke={theme.jiaying.text.tertiary}
                  strokeDasharray="2 3"
                />
              ))}
              <Line
                type="linear"
                dataKey="value"
                // 🔴 中性强调色（分类色板首色）—— 不用红绿：家底不是"涨跌"
                stroke={theme.jiaying.chart[0]}
                strokeWidth={2}
                // 🔴 不做曲线平滑：`monotone` 会在两点之间造出"中间值"，而那不是真实数据
                dot={{ r: 2.5 }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
}
